// src/pages/Register.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as z from 'zod';

import { Button } from 'components/Elements';
import { Form, InputField, PhoneInputField } from 'components/Form';
import { useAuth } from 'lib/auth';
import { Link, useNavigate } from 'react-router-dom';
import { AccountLayout } from 'components/Layout';

const schema = z
  .object({
    name: z.string().min(1, 'Full name is required'),
    email: z.string().email('Enter a valid email').min(1, 'Email is required'),
    phoneNumber: z.string().min(1, 'Phone number is required'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    terms: z.boolean().refine((val) => val === true, 'You must agree to the terms'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export function Register() {
  const [loading, setLoading] = useState(false);
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  return (
    <AccountLayout
      title="Create Account"
      description="Sell unused items or discover nearby deals in minutes."
    >
      <Form
        onSubmit={async (values) => {
          setLoading(true);
          try {
            await registerUser({
              username: values.username,
              email: values.email,
              phoneNumber: values.phoneNumber,
              password: values.password,
            });
            navigate('/app');
          } catch (error) {
            setLoading(false);
          }
        }}
        schema={schema}
      >
        {({ register, formState }) => (
          <div className="flex flex-col space-y-4">
            <InputField
              label="Username"
              type="text"
              error={formState.errors['username']}
              registration={register('username')}
              placeholder="Username"
            />

            <InputField
              label="Email Address"
              type="email"
              error={formState.errors['email']}
              registration={register('email')}
              placeholder="Email address"
            />

            <PhoneInputField
              label="Phone number"
              type="text"
              error={formState.errors['phoneNumber']}
              registration={register('phoneNumber')}
              placeholder="Phone number"
            />

            <InputField
              label="Password"
              type="password"
              error={formState.errors['password']}
              registration={register('password')}
              placeholder="Password"
            />

            {formState.errors['terms'] && (
              <p className="text-sm text-red-500 mt-1">{formState.errors['terms'].message}</p>
            )}

            <Button isLoading={loading} type="submit" className="w-full mt-4">
              Continue
            </Button>
            <div className="text-center">
              <div className="relative flex items-center justify-center my-3">
                <div className="absolute w-full border-t border-gray-300" />
                <span className="relative bg-white px-3 text-xs text-gray-500">or</span>
              </div>
              <Link to="/auth/login" className="text-[#0f3c0d]">
                Already have an account? Sign in
              </Link>
            </div>
          </div>
        )}
      </Form>
    </AccountLayout>
  );
}

Register.propTypes = {
  onSuccess: PropTypes.func,
};
